import type { ReportDispositionReasonV2 } from "@libs/Conversations/classification";
import type {
  CallLabels,
  CallOutcome,
  Company,
  Conversation,
  DispositionSource,
  DispositionV2,
} from "@types";
import { DISPOSITION_V2_VALUES } from "@types";

export type {
  ReportDispositionCategoryV2,
  ReportDispositionReasonV2,
} from "@libs/Conversations/classification";

import type { KeyTakeaway } from "./types/keyTakeaways";

/**
 * Rolling look-back window presets. Values match dale's `ReportWindowPreset`
 * enum on the wire (SD-10001): `1d` = 24h, `7d`/`30d`/`90d` = the trailing N
 * days. These mirror the Industry Insights filter vocabulary so both surfaces
 * offer the same look-back options. The backend frames each window; the FE only
 * sends the preset key. Custom ranges are a separate selection mode, not a preset.
 */
export const REPORT_WINDOW_PRESETS = ["1d", "7d", "30d", "90d"] as const;

export type ReportWindowPreset = (typeof REPORT_WINDOW_PRESETS)[number];

/** New vs returning at the time of a call (T4 / SD-9881). */
export type CallerType = "new" | "returning";

/** Reports-compatible alias of the shared Dale disposition vocabulary. */
export const REPORT_DISPOSITION_V2_VALUES = DISPOSITION_V2_VALUES;

/**
 * Migration shim. The shared `Conversation` contract has now been corrected to
 * dale's real (scalar) `LabelSet` shape and its full seven-value `Disposition`
 * enum, so these Reports-local types no longer describe anything different —
 * they are aliases purely to spare ~40 call sites a rename. Follow-up: drop the
 * shim and use the `@types` names directly.
 */
export type ReportDispositionV2 = DispositionV2;
export type ReportCallOutcome = CallOutcome;
export type ReportCallLabels = CallLabels;

export type { DispositionSource };

/**
 * Per-call disposition provenance carried by a drilldown row and returned by the
 * override PATCH (SD-10276). Shared so the row type and the mutation response stay
 * in lock-step with the backend contract. The disposition value itself now rides on
 * `dispositionV2` / `reportDispositionV2`; these fields are its override provenance.
 */
type ConversationDisposition = {
  /** Defaults `auto`; comes back `manual` after a manual override. */
  dispositionSource?: DispositionSource;
  /** User id of the overrider; null until manually overridden. */
  dispositionOverriddenBy?: string | null;
  /** ISO datetime of the override; null until manually overridden. */
  dispositionOverriddenAt?: string | null;
};

/**
 * One normalized row of a metric drill-down
 * (`GET /companies/{id}/reports/conversations`). The current classification
 * fields use report-local names so the row remains assignable to `Conversation`
 * for the drawer without replacing its shared `callLabels` shape.
 */
export type ReportDrilldownConversation = Conversation &
  ConversationDisposition & {
    /** New vs returning at the time of this call. Null when no linked customer. */
    callerType?: CallerType | null;
    reportCallLabels?: ReportCallLabels | null;
    reportDispositionV2?: ReportDispositionV2 | null;
    /**
     * Revenue attributed to this one call, in USD (SD-13689). Dale resolves the
     * whole precedence server-side — ServiceTitan invoice actual, else the
     * captured price projection, else the company's flat contract-value estimate
     * — over the same win-call population the Estimated revenue tile sums, so
     * summing this column across a `win_calls` drill-down reproduces the tile.
     *
     * Never re-derive any of that here: applying the contract-value fallback
     * client-side would re-implement dale's `win_call_logic` classifier in
     * TypeScript and drift from the tile the column sits under.
     *
     * Three states, and none of them is `0`. Absent means dale has not shipped
     * the field yet; `null` means it shipped and this call has no attributable
     * revenue (not a win); a number is real data. Both non-numbers render an em
     * dash, so a non-booked call never claims "$0 of revenue". `0` is itself a
     * legitimate value — a zero-dollar invoice, or a company that configured its
     * estimated contract value to $0.
     */
    revenueUsd?: number | null;
  };

/** Unnormalized drilldown row exactly as Dale sends it on the wire. */
export type ReportDrilldownWireConversation = Omit<
  ReportDrilldownConversation,
  "callLabels" | "dispositionV2" | "reportCallLabels" | "reportDispositionV2"
> & {
  callLabels?: ReportCallLabels | null;
  dispositionV2?: ReportDispositionV2 | null;
};

/**
 * The window a dashboard shows: live data (only where the window picker offers
 * a Live entry), one of its rolling presets, or a custom range of inclusive
 * local calendar dates (`YYYY-MM-DD`).
 */
export type WindowSelection<P extends string> =
  | { mode: "live" }
  | { mode: "preset"; preset: P }
  | { mode: "custom"; start: string; end: string };

/**
 * The active Reports window. Either one of the rolling-window presets
 * (`1d`/`7d`/`30d`/`90d`, resolved server-side, deep-linkable via the
 * `/reports/:preset` route) or a user-picked custom
 * range. For custom, `start` / `end` are **inclusive local
 * calendar dates** (`YYYY-MM-DD`) sent to T1's
 * `GET /companies/{id}/reports/v2/custom?start=&end=` (SD-9878); the backend frames
 * the window in the company timezone.
 */
export type ReportWindowSelection = Exclude<
  WindowSelection<ReportWindowPreset>,
  { mode: "live" }
>;

/**
 * Serialization format for a downloadable Report hub export. Shared by the
 * campaign and call-breakdown exports, which take the same `?format=` param off
 * one backend enum (dale `CampaignReportExportFormat`).
 */
export const REPORT_EXPORT_FORMATS = ["xlsx", "csv"] as const;
export type ReportExportFormat = (typeof REPORT_EXPORT_FORMATS)[number];

/**
 * New-vs-returning caller-mix counts (T3 / SD-9880). Counts are distinct callers
 * over the window, not calls. There is intentionally no `total` on the wire — sum
 * the two client-side when needed.
 */
export type CallerMixResponse = {
  newCallers: number;
  returningCallers: number;
};

export type ReportMetricTrend = {
  currentValue: number;
  previousValue: number;
  percentageChange: number;
};

/**
 * Per-window call-status breakdown. The wire keys are unchanged, but under the
 * new classification (C7 / SD-10736) these counts are re-sourced BE-side from the
 * new Outcome / Call-Driver dimensions (`scheduled`/`lead`/`transferred` ← Outcome,
 * `emergency` ← Call-Driver, `support`/`other` ← rollups) rather than the legacy
 * `customer.status`. The FE shape stays wire-compatible.
 */
export type ReportStatusBreakdown = {
  scheduled: number;
  lead: number;
  support: number;
  emergency: number;
  transferred: number;
  other: number;
};

/**
 * Call-duration percentiles (seconds) for one window, projected from the backend's
 * `call_duration` t-digest sketch (SD-10104). Each value is `null` when the window
 * had no duration observations. Replaces the former `avgDurationSeconds`.
 */
export type ReportDurationPercentiles = {
  p50: number | null;
  p90: number | null;
  p95: number | null;
};

/**
 * Booking channel a realized (invoice-actual) job is attributed to — its
 * earliest in-window booking source (SD-14688 / dale#8410, `RealizedRevenueSource`).
 * Booking wizard wins exact ties. Wire values are snake_case.
 */
export type RealizedRevenueSource =
  | "ai_calls"
  | "chat_widget"
  | "sms"
  | "campaigns"
  | "booking_wizard";

/**
 * Five-point summary of an invoice-backed job-value sketch, in USD **dollars**
 * (not cents). Present only when the window has invoice-backed jobs — see
 * `ReportRealizedRevenueDistribution`.
 */
export type ReportRealizedRevenueSummary = {
  min: number;
  p25: number;
  p50: number;
  p80: number;
  max: number;
};

/** One booking channel's realized-revenue summary. */
export type ReportRealizedRevenueBySource = ReportRealizedRevenueSummary & {
  source: RealizedRevenueSource;
};

/**
 * Realized (invoice-actual) per-job revenue distribution for the window: an
 * overall five-point summary plus a per-channel breakdown. `bySource` **omits**
 * channels with no invoiced jobs (BE-ordered by `RealizedRevenueSource`), so the
 * UI renders only channels that actually have data — never assume all five.
 */
export type ReportRealizedRevenueDistribution = ReportRealizedRevenueSummary & {
  bySource: ReportRealizedRevenueBySource[];
};

/**
 * The interaction channels the Report hub "Interaction Breakdown" strip splits
 * on. Mirrors the backend `ReportInteractionChannel` enum: the `/reports/v2`
 * payload emits one entry per conversation type that composes `totalInteractions`,
 * so the channel counts sum to that headline. The FE groups them into display
 * rows (campaign channels fold into their parent) — see `buildInteractionBreakdownRows`.
 */
export type ReportInteractionChannel =
  | "inbound"
  | "outbound"
  | "web_call"
  | "inbound_sms"
  | "outbound_sms"
  | "booking_wizard"
  | "chat_widget"
  | "campaign_inbound_call"
  | "campaign_outbound_sms";

/** One channel's interaction count + share for the Interaction Breakdown strip. */
export type ReportInteractionBreakdownEntry = {
  channel: ReportInteractionChannel;
  count: number;
  percentage: number;
};

/**
 * One row of any part-to-whole breakdown: a stable key, a display-ready label,
 * and its count. The shape every breakdown chart on these pages takes — the
 * donut, and the stacked bar it replaced — so a caller can move a breakdown
 * between them without reshaping its data.
 */
export type BreakdownSegment = {
  key: string;
  label: string;
  count: number;
  /** Optional tooltip for the legend label (e.g. a reason definition). */
  definition?: string;
};

export type ReportKpis = {
  calls: number;
  /**
   * All direct + campaign call/SMS interactions for the window (inbound,
   * outbound, campaign inbound/outbound, live direct outbound SMS). Forward-only:
   * `null` until the backend serializes it, so consumers fall back to `calls`
   * (inbound-only). Distinct from `aiCalls.metrics.totalInteractions`, which is
   * voice-call-only.
   */
  totalInteractions?: number | null;
  bookingRate: number | null;
  durationPercentiles: ReportDurationPercentiles;
  statusBreakdown: ReportStatusBreakdown;
  qualifiedCalls: number;
  bookedCalls: number;
  totalMinutes: number | null;
  /**
   * Per-channel interaction counts for the Report hub Interaction Breakdown
   * strip. Forward-only: the `/reports/v2` backend does not serialize this yet,
   * so the normalizer defaults it to `[]` and the UI renders a "not available
   * yet" state until the backend ships it.
   */
  interactionBreakdown: ReportInteractionBreakdownEntry[];
  /**
   * Estimated booking revenue (USD) for the window — the sum of booked calls'
   * captured quoted prices (SD-10434). Forward-only: `null` when the backend has
   * no revenue signal for the window (calls before the metric shipped carry no
   * price). See `metricStatuses.revenue_usd_v2` for window completeness.
   */
  revenueUsd: number | null;
  /**
   * Realized (invoice-actual) per-job revenue distribution (SD-14688 / dale#8410),
   * on the v2 account report only — the combined slice and each per-company member.
   * `null` when the window has no invoice-backed jobs (ServiceTitan-only in
   * practice, so the field self-gates). The standalone single-company endpoint
   * (`in_app_reports.py`) does not populate it yet, so it arrives `null` there
   * until a BE follow-up ships.
   */
  realizedRevenueDistribution: ReportRealizedRevenueDistribution | null;
};

export type ReportDailyStatsEntry = {
  date: string;
  totalCalls: number;
  qualifiedCalls: number;
  bookedCalls: number;
  qualifiedBookingRate: number;
  supportCalls: number;
  emergencyCalls: number;
  /**
   * Calls per outcome label for this day (SD-13001), keyed by the backend enum
   * value so it maps 1:1 onto the `outcome` dimension rows.
   *
   * A label with no calls that day is OMITTED, not sent as `0` — read a missing
   * key as zero. Absent entirely on payloads that predate the field.
   */
  outcomes?: Record<string, number>;
};

/**
 * Period-over-period change for each call-duration percentile (SD-10104). Each is
 * `null` when either window lacks a defined percentile. Replaces the former
 * `avgDurationSeconds` trend.
 */
export type ReportDurationPercentileTrends = {
  p50?: ReportMetricTrend | null;
  p90?: ReportMetricTrend | null;
  p95?: ReportMetricTrend | null;
};

export type ReportTrends = {
  calls: ReportMetricTrend;
  bookingRate?: ReportMetricTrend | null;
  durationPercentiles?: ReportDurationPercentileTrends;
  qualifiedCalls?: ReportMetricTrend | null;
  bookedCalls?: ReportMetricTrend | null;
  totalMinutes?: ReportMetricTrend | null;
  /** Booking-revenue (USD) total, period-over-period (SD-10434). */
  revenueUsd?: ReportMetricTrend | null;
};

/**
 * Per-metric data-completeness for the selected report window (SD-10433). Lets a
 * forward-only / partially-backfilled metric (e.g. booking revenue) flag a window
 * that predates its data instead of showing a misleadingly-low sum as complete.
 */
export type MetricWindowStatus = "available" | "incomplete" | "no_data";

export type ReportMetricDataStatus = {
  status: MetricWindowStatus;
  /**
   * Earliest UTC hour the metric carries data; `null` when the metric has no
   * forward-only boundary (treated as available for the whole window).
   */
  dataAvailableFromHourUtc: string | null;
};

export type ReportFreshness = {
  /**
   * UTC watermark for the last completed rollup hour. `null` (or an epoch-zero
   * sentinel) means no data has rolled up for the window yet — see
   * `hasReportWatermark`.
   */
  lastCompletedHourUtc: string | null;
};

export type ReportWindow = {
  /** Rolling preset, or the literal echo returned by custom-window endpoints. */
  preset: ReportWindowPreset | "custom";
  startDate: string;
  endDate: string;
  timezone: string;
};

export const BOOKING_WIZARD_FUNNEL_STEP_NAMES = [
  "contactAndAddressCompleted",
  "treeCompleted",
  "availabilityLoaded",
  "slotSelected",
  "bookingAttempted",
  "booked",
] as const;

export type BookingWizardFunnelStepName =
  (typeof BOOKING_WIZARD_FUNNEL_STEP_NAMES)[number];

export type BookingWizardFunnelStep = {
  step: BookingWizardFunnelStepName;
  reached: number;
  stepThroughRate: number | null;
  abandonedHere: number;
  stillActiveHere: number;
};

export type BookingWizardTradeCount = {
  trade: string | null;
  count: number;
};

export type BookingWizardReportResponse = {
  window: ReportWindow;
  total: number;
  booked: number;
  bookingFailed: number;
  abandoned: number;
  inProgress: number;
  conversionRate: number | null;
  failureRate: number | null;
  abandonmentRate: number | null;
  funnel: BookingWizardFunnelStep[];
  tradeBreakdown: BookingWizardTradeCount[];
  /**
   * Backend-computed benchmark takeaways (SD-11887). Empty today — Booking Wizard
   * metrics have no industry benchmark yet — but the field ships so the panel
   * lights up automatically as coverage grows.
   */
  keyTakeaways: KeyTakeaway[];
};

/**
 * A single Booking Wizard session's terminal / current disposition. The session
 * drill-down `status` filter re-scopes the list to one of these; it mirrors the
 * Booking Wizard report's headline metric split (SD-11593).
 */
export type BookingWizardSessionStatus =
  | "booked"
  | "bookingFailed"
  | "abandoned"
  | "inProgress";

/**
 * One row of a Booking Wizard session drill-down
 * (`GET /companies/{id}/reports/booking-wizard/{preset}/sessions`, SD-11593).
 * Every field is camelCase; nullable wire fields are `null` (never `undefined`).
 */
export type BookingWizardSessionRow = {
  id: string;
  conversationId: string;
  customerName: string;
  phone: string;
  requestedAddress: string;
  status: BookingWizardSessionStatus;
  lastCompletedStep: BookingWizardFunnelStepName;
  trade: string;
  serviceLabel: string;
  issueDescription: string;
  selectedSlot: string | null;
  availableSpotsCount: number | null;
  serviceTitanStatus: string;
  serviceTitanJobId: number | null;
  createdAt: string;
  bookedAt?: string | null;
  revenue?: number | null;
};

export type InAppReportResponse = {
  window: ReportWindow;
  kpis: ReportKpis;
  dailyStats: ReportDailyStatsEntry[];
  trends: ReportTrends;
  freshness: ReportFreshness;
  /**
   * Per-metric window completeness (SD-10433), keyed by the backend registry
   * metric-id (snake_case, e.g. `revenue_usd_v2`). Empty for older payloads that
   * predate the field.
   */
  metricStatuses: Record<string, ReportMetricDataStatus>;
};

/**
 * Disposition breakdown (Phase G), served by the `/reports/v2/{preset}` endpoint
 * (SD-11016). `bookingRateV2 = win / (win + unresolved) * 100` (a percentage, `null`
 * when that denominator is 0); `unqualified`, `supportResolved`,
 * `supportUnresolved`, and `noContact` are OUTSIDE the denominator. `reasonCounts`
 * is the union of the Unresolved-reason + Disqualifier axes (the wire key is
 * `reasons`).
 */
export type ReportDispositionBreakdownV2Response = {
  win: number;
  unresolved: number;
  unqualified: number;
  noContact: number;
  supportResolved: number;
  supportUnresolved: number;
  unclassified: number;
  bookingRateV2: number | null;
  /** Reason machine-string → count. Sparse: zero-count reasons may be omitted. */
  reasonCounts: Partial<Record<ReportDispositionReasonV2, number>>;
};

/**
 * Period-over-period change for the disposition categories + booking rate. Category
 * trends are always present; `bookingRateV2` is `null` when either window lacks a
 * defined rate.
 */
export type ReportDispositionV2Trends = {
  win?: ReportMetricTrend | null;
  unresolved?: ReportMetricTrend | null;
  qualifiedCallsV2?: ReportMetricTrend | null;
  bookingRateV2?: ReportMetricTrend | null;
};

/**
 * v2 trends envelope — the same headline trends as `ReportTrends` plus the
 * disposition trend (`dispositionV2`).
 */
export type ReportTrendsV2 = ReportTrends & {
  dispositionV2?: ReportDispositionV2Trends;
};

/**
 * v2 in-app report payload (SD-11016) — the `/reports/v2/{preset}` counterpart of
 * `InAppReportResponse`. Identical envelope (window, kpis, dailyStats, freshness,
 * metricStatuses), plus the `dispositionV2` block (always present) and a
 * v2-shaped `trends`. NOTE: `kpis.bookingRate`/`qualifiedCalls`/`bookedCalls` are
 * still legacy-computed on this endpoint — the v2 headline values come from
 * `dispositionV2` (see `withDispositionV2Kpis`).
 */
export type InAppReportResponseV2 = Omit<InAppReportResponse, "trends"> & {
  dispositionV2: ReportDispositionBreakdownV2Response;
  trends: ReportTrendsV2;
  /**
   * Backend-computed benchmark takeaways (SD-11887). Empty when no covered metric
   * falls outside its industry quartile for the window — a normal state.
   */
  keyTakeaways: KeyTakeaway[];
};

/** One member company's slice of an account-wide report — `report` is v2. */
export type AccountCompanyReportV2 = {
  /**
   * THE trustworthy company status source in this app (SD-12994).
   *
   * dale builds each per-company slice from the real `CompanyDocument` via
   * `CompanyLite.model_validate(company, from_attributes=True)`, so `status`
   * here is the company's actual lifecycle status.
   *
   * `account.companies[].status` — what `useCanViewMultiCompany().companies`
   * returns — is NOT. `account_aggregation_pipeline` projects only `_id`,
   * `name`, `created_at`, `phone`, while `CompanyLite` declares
   * `status: CompanyStatus = Field(default=CompanyStatus.INACTIVE)`, so
   * `GET /accounts/{id}` serializes `"inactive"` for *every* company. That is a
   * wrong value, not a missing field: the client cannot detect it. Never narrow
   * this back to `Pick<Company, "_id" | "name">` and read status off the account
   * list instead.
   *
   * `status` is optional because it may be absent or unrecognized on the wire —
   * `normalizeAccountReportResponseV2` coerces anything outside
   * `COMPANY_STATUSES` to `undefined`, which means *unknown*, not *inactive*.
   */
  company: Pick<Company, "_id" | "name" | "status">;
  report: InAppReportResponseV2;
  callerMix: CallerMixResponse;
  /**
   * Campaign goals completed in the window for this location (SD-13283).
   *
   * **Three states, three meanings — none of them `0`.** `undefined` means the
   * field is absent from the payload because dale hasn't shipped it yet, and the
   * overview tile reads "Coming soon". `null` means the field shipped and this
   * company has no campaigns expansion, so it contributes nothing and the tile
   * hides when *every* location says `null`. A number is real data.
   *
   * The `null` state is what makes the expansion gate self-describing from the
   * payload — and it has to be: `useCanViewMultiCompany().companies` comes from
   * the account-list projection, which carries no `expansion`, so the client
   * cannot compute that gate itself. Never default either state to `0`: that
   * would report "no goals completed" for a location that isn't running
   * campaigns at all.
   */
  campaignGoalsCompleted?: number | null;
  /**
   * Booking Wizard sessions that ended `booked` for this location (SD-13283).
   * Same three-state contract as `campaignGoalsCompleted`, keyed off the
   * `bookingWizard` expansion.
   */
  bookingWizardBooked?: number | null;
};

/**
 * v2 account aggregate (SD-11016) — the payload of
 * `/accounts/{id}/reports/v2/{preset}`. Every per-company slice and the
 * combined slice carry `dispositionV2` (always present) + the v2 trends shape.
 *
 * Named for the scope it has: `perCompany` covers the companies of ONE account,
 * never several. A genuinely portfolio-scoped payload (spanning accounts) would
 * be a separate type. See AGENTS.md § Domain Model.
 */
export type AccountReportResponseV2 = {
  window: ReportWindow;
  perCompany: AccountCompanyReportV2[];
  combined: {
    kpis: ReportKpis;
    callerMix: CallerMixResponse;
    trends: ReportTrendsV2;
    dispositionV2: ReportDispositionBreakdownV2Response;
    /** Per-metric window completeness for the summed slice (SD-10433). */
    metricStatuses: Record<string, ReportMetricDataStatus>;
    /**
     * Account-level benchmark takeaways (SD-11887). A mixed-industry rollup can't
     * be compared to a single cohort, so this stays empty; per-company slices
     * carry their own on `perCompany[].report.keyTakeaways`.
     */
    keyTakeaways: KeyTakeaway[];
  };
  freshness: ReportFreshness;
};
